import {
  defineStaffFeatureEnablement,
  defineUserFeatureEnablement,
  defineStaffSetting,
  defineUserSetting,
  type Setting,
  type CustomerScope,
} from "./types";

/**
 * Every setting in Walliyo, in one list (WALLIYO-0066).
 *
 * To add a setting:
 *   1. add it here with the define function for its kind. Its `settingId` is
 *      <NAME>_<USER|STAFF>_<SETTING|FEATURE_ENABLEMENT>, e.g.
 *      QUOTE_JOBS_USER_FEATURE_ENABLEMENT. It is permanent: choose the name
 *      carefully, because stored values and the audit log keep it forever;
 *   2. if its value type is new, add it to VALUE_TYPES in value-types.ts;
 *   3. add its name and description to the translation files under its
 *      settingId.
 * Nothing else creates a setting. `definitions.t.ts` checks that every
 * `settingId` is unique and fits its kind.
 */

/**
 * Where a client feature can be switched on: everyone down to one client
 * profile, or one person. `user` is how a ban reaches a client feature
 * (ADR-0013 §5): client profiles can't hold a value yet.
 */
const CLIENT_FEATURE: readonly ("system" | CustomerScope)[] = [
  "system",
  "region",
  "appVersion",
  "allClients",
  "clientProfile",
  "user",
];

/** Where a pro feature can be switched on: everyone down to one pro profile. */
const PRO_FEATURE: readonly ("system" | CustomerScope)[] = ["system", "region", "appVersion", "allPros", "proProfile"];

/** Where a feature for clients and pros alike can be switched on: everyone down to one person. */
const CUSTOMER_FEATURE: readonly ("system" | CustomerScope)[] = [
  "system",
  "region",
  "appVersion",
  "allClients",
  "allPros",
  "user",
];

export const SETTINGS = [
  // ── User settings: appearance ────────────────────────────────────────────
  defineUserSetting({
    settingId: "THEME_USER_SETTING",
    name: "Theme",
    category: "appearance",
    scopes: ["user"],
    valueType: "Theme",
    isList: false,
    defaultValue: "system",
  }),
  defineUserSetting({
    settingId: "COLOR_VIBE_USER_SETTING",
    name: "Colour",
    category: "appearance",
    scopes: ["user"],
    valueType: "ColorVibe",
    isList: false,
    defaultValue: "preferred",
  }),
  defineUserSetting({
    settingId: "LANGUAGE_USER_SETTING",
    name: "Language",
    category: "appearance",
    scopes: ["user"],
    valueType: "Language",
    isList: false,
    defaultValue: "fr",
  }),
  // No images and lighter screens, for people paying for data by the hour.
  defineUserSetting({
    settingId: "LOW_DATA_MODE_USER_SETTING",
    name: "Low data mode",
    category: "appearance",
    scopes: ["user"],
    valueType: "Boolean",
    isList: false,
    defaultValue: false,
  }),

  // ── User settings: notifications ─────────────────────────────────────────
  // In-app notifications, never SMS: SMS is only for the codes someone needs
  // before they can open the app (WALLIYO-0098). The *_SMS_* settings they
  // replace are retired.
  defineUserSetting({
    settingId: "NEW_LEAD_NOTIFICATION_USER_SETTING",
    name: "Notify me of new leads",
    category: "notifications",
    scopes: ["proProfile"],
    valueType: "Boolean",
    isList: false,
    defaultValue: true,
  }),
  defineUserSetting({
    settingId: "QUOTE_RECEIVED_NOTIFICATION_USER_SETTING",
    name: "Notify me when a quote arrives",
    category: "notifications",
    scopes: ["clientProfile"],
    valueType: "Boolean",
    isList: false,
    defaultValue: true,
  }),
  defineUserSetting({
    settingId: "NEW_LEAD_SMS_USER_SETTING",
    name: "Send new leads by SMS",
    category: "notifications",
    scopes: ["proProfile"],
    valueType: "Boolean",
    isList: false,
    defaultValue: true,
    status: "retired",
  }),
  defineUserSetting({
    settingId: "QUOTE_RECEIVED_SMS_USER_SETTING",
    name: "Send a quote by SMS",
    category: "notifications",
    scopes: ["clientProfile"],
    valueType: "Boolean",
    isList: false,
    defaultValue: true,
    status: "retired",
  }),

  // ── User settings: which leads a pro wants ───────────────────────────────
  defineUserSetting({
    settingId: "LEAD_TRADES_USER_SETTING",
    name: "Trades I want leads for",
    category: "leads",
    scopes: ["proProfile"],
    valueType: "Trade",
    isList: true,
    defaultValue: [],
  }),
  defineUserSetting({
    settingId: "LEAD_SERVICE_AREA_USER_SETTING",
    name: "Where I work",
    category: "leads",
    scopes: ["proProfile"],
    valueType: "ShortText",
    isList: false,
    defaultValue: "",
  }),
  defineUserSetting({
    settingId: "LEAD_RADIUS_USER_SETTING",
    name: "How far I travel",
    category: "leads",
    scopes: ["proProfile"],
    valueType: "Kilometres",
    isList: false,
    defaultValue: 10,
  }),
  defineUserSetting({
    settingId: "LEAD_DAYS_USER_SETTING",
    name: "Days I take work",
    category: "leads",
    scopes: ["proProfile"],
    valueType: "Weekday",
    isList: true,
    defaultValue: ["mon", "tue", "wed", "thu", "fri"],
  }),
  defineUserSetting({
    settingId: "URGENT_JOBS_USER_SETTING",
    name: "Urgent jobs",
    category: "leads",
    scopes: ["proProfile"],
    valueType: "Boolean",
    isList: false,
    defaultValue: true,
    // Nothing can mark a job urgent: there is no urgent column on jobs and
    // nothing in the post payload. Its client-side half,
    // URGENT_JOB_USER_FEATURE_ENABLEMENT, has always been planned; this side
    // said active and offered a pro a switch that could never do anything
    // (WALLIYO-0109).
    status: "planned",
  }),

  // ── Staff settings ───────────────────────────────────────────────────────
  defineStaffSetting({
    settingId: "CONSOLE_THEME_STAFF_SETTING",
    name: "Console theme",
    category: "appearance",
    valueType: "Theme",
    isList: false,
    defaultValue: "system",
  }),
  defineStaffSetting({
    settingId: "CONSOLE_LANGUAGE_STAFF_SETTING",
    name: "Console language",
    category: "appearance",
    valueType: "Language",
    isList: false,
    defaultValue: "fr",
  }),

  // ── Feature enablements for staff: accounts & support ───────────────────
  defineStaffFeatureEnablement({
    settingId: "SEARCH_ACCOUNTS_STAFF_FEATURE_ENABLEMENT",
    name: "Search accounts",
    category: "accounts",
    sensitivity: "low",
  }),
  defineStaffFeatureEnablement({
    settingId: "VIEW_ACCOUNTS_STAFF_FEATURE_ENABLEMENT",
    name: "View an account",
    category: "accounts",
    sensitivity: "low",
  }),
  defineStaffFeatureEnablement({
    settingId: "REVEAL_CONTACT_DETAILS_STAFF_FEATURE_ENABLEMENT",
    name: "Reveal a phone number",
    category: "accounts",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "EDIT_CUSTOMER_SETTINGS_STAFF_FEATURE_ENABLEMENT",
    name: "Edit a customer's settings",
    category: "accounts",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "CLAIM_CASES_STAFF_FEATURE_ENABLEMENT",
    name: "Claim cases",
    category: "support",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "REASSIGN_CASES_STAFF_FEATURE_ENABLEMENT",
    name: "Reassign cases",
    category: "support",
    sensitivity: "high",
  }),

  // ── Feature enablements for staff: on behalf of a customer ──────────────
  defineStaffFeatureEnablement({
    settingId: "ACT_ON_BEHALF_STAFF_FEATURE_ENABLEMENT",
    name: "Act on behalf of a customer",
    category: "onBehalf",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "CORRECT_ACCOUNT_DETAILS_STAFF_FEATURE_ENABLEMENT",
    name: "Correct account details",
    category: "onBehalf",
    sensitivity: "high",
  }),

  // ── Feature enablements for staff: onboarding ───────────────────────────
  defineStaffFeatureEnablement({
    settingId: "REVIEW_VERIFICATIONS_STAFF_FEATURE_ENABLEMENT",
    name: "Review verifications",
    category: "onboarding",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "VIEW_ID_IMAGES_STAFF_FEATURE_ENABLEMENT",
    name: "View ID images",
    category: "onboarding",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "APPROVE_PROS_STAFF_FEATURE_ENABLEMENT",
    name: "Approve pros",
    category: "onboarding",
    sensitivity: "high",
  }),

  // ── Feature enablements for staff: payments ─────────────────────────────
  defineStaffFeatureEnablement({
    settingId: "VIEW_PAYMENTS_STAFF_FEATURE_ENABLEMENT",
    name: "View payments",
    category: "payments",
    sensitivity: "low",
  }),
  defineStaffFeatureEnablement({
    settingId: "ISSUE_REFUNDS_STAFF_FEATURE_ENABLEMENT",
    name: "Issue refunds",
    category: "payments",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "ISSUE_CREDITS_STAFF_FEATURE_ENABLEMENT",
    name: "Issue credits",
    category: "payments",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "WAIVE_FEES_STAFF_FEATURE_ENABLEMENT",
    name: "Waive fees",
    category: "payments",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "MARK_PAYMENTS_PAID_STAFF_FEATURE_ENABLEMENT",
    name: "Mark a payment as paid",
    category: "payments",
    sensitivity: "high",
  }),
  // The commercial console (ADR-0018 §4a). Each one writes, so each is high.
  defineStaffFeatureEnablement({
    settingId: "MANAGE_PLAN_PRICES_STAFF_FEATURE_ENABLEMENT",
    name: "Set plan prices",
    category: "payments",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "RUN_PROMOS_STAFF_FEATURE_ENABLEMENT",
    name: "Run promotions",
    category: "payments",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "OFFER_DISCOUNTS_STAFF_FEATURE_ENABLEMENT",
    name: "Offer discounts",
    category: "payments",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "MANAGE_SUBSCRIPTIONS_STAFF_FEATURE_ENABLEMENT",
    name: "Extend or end a subscription",
    category: "payments",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "APPROVE_PAYMENT_REQUESTS_STAFF_FEATURE_ENABLEMENT",
    name: "Approve above the limit",
    category: "payments",
    sensitivity: "critical",
  }),
  defineStaffFeatureEnablement({
    settingId: "RECONCILE_PAYMENTS_STAFF_FEATURE_ENABLEMENT",
    name: "Reconcile payments",
    category: "payments",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "EXPORT_PAYMENTS_STAFF_FEATURE_ENABLEMENT",
    name: "Export",
    category: "payments",
    sensitivity: "high",
  }),

  // ── Feature enablements for staff: trust & safety ───────────────────────
  defineStaffFeatureEnablement({
    settingId: "HANDLE_REPORTS_STAFF_FEATURE_ENABLEMENT",
    name: "Handle reports",
    category: "safety",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "SET_REPORT_SEVERITY_STAFF_FEATURE_ENABLEMENT",
    name: "Set a report's severity",
    category: "safety",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "SUSPEND_PENDING_INVESTIGATION_STAFF_FEATURE_ENABLEMENT",
    name: "Temporary suspension",
    category: "safety",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "ISSUE_VERDICTS_STAFF_FEATURE_ENABLEMENT",
    name: "Issue a verdict",
    category: "safety",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "BAN_PERMANENTLY_STAFF_FEATURE_ENABLEMENT",
    name: "Ban permanently",
    category: "safety",
    sensitivity: "critical",
  }),
  defineStaffFeatureEnablement({
    settingId: "HANDLE_APPEALS_STAFF_FEATURE_ENABLEMENT",
    name: "Handle appeals",
    category: "safety",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "REFER_TO_POLICE_STAFF_FEATURE_ENABLEMENT",
    name: "Refer to the police",
    category: "safety",
    sensitivity: "critical",
  }),
  defineStaffFeatureEnablement({
    settingId: "SHARE_DATA_WITH_POLICE_STAFF_FEATURE_ENABLEMENT",
    name: "Share data with the police",
    category: "safety",
    sensitivity: "critical",
  }),
  defineStaffFeatureEnablement({
    settingId: "PLACE_LEGAL_HOLDS_STAFF_FEATURE_ENABLEMENT",
    name: "Preserve evidence",
    category: "safety",
    sensitivity: "critical",
  }),
  defineStaffFeatureEnablement({
    settingId: "MODERATE_REVIEWS_STAFF_FEATURE_ENABLEMENT",
    name: "Moderate reviews",
    category: "safety",
    sensitivity: "high",
  }),

  // ── Feature enablements for staff: messaging ────────────────────────────
  // Staff don't text customers: replies go in the app, with a notification
  // (WALLIYO-0098). Retired, kept for the audit log's old grants.
  defineStaffFeatureEnablement({
    settingId: "SEND_SMS_STAFF_FEATURE_ENABLEMENT",
    name: "Send an SMS",
    category: "messaging",
    sensitivity: "low",
    status: "retired",
  }),
  defineStaffFeatureEnablement({
    settingId: "SEND_BULK_SMS_STAFF_FEATURE_ENABLEMENT",
    name: "Send bulk SMS",
    category: "messaging",
    sensitivity: "high",
    status: "retired",
  }),
  defineStaffFeatureEnablement({
    settingId: "EDIT_SMS_TEMPLATES_STAFF_FEATURE_ENABLEMENT",
    name: "Edit SMS templates",
    category: "messaging",
    sensitivity: "high",
  }),

  // ── Feature enablements for staff: configuration ────────────────────────
  defineStaffFeatureEnablement({
    settingId: "MANAGE_APP_FEATURES_STAFF_FEATURE_ENABLEMENT",
    name: "Manage app features",
    category: "configuration",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "EDIT_RULES_STAFF_FEATURE_ENABLEMENT",
    name: "Edit rules",
    category: "configuration",
    sensitivity: "critical",
  }),
  defineStaffFeatureEnablement({
    settingId: "EDIT_PRICING_STAFF_FEATURE_ENABLEMENT",
    name: "Edit pricing",
    category: "configuration",
    sensitivity: "critical",
  }),
  defineStaffFeatureEnablement({
    settingId: "EDIT_CATEGORIES_STAFF_FEATURE_ENABLEMENT",
    name: "Edit trades",
    category: "configuration",
    sensitivity: "high",
  }),

  // ── Feature enablements for staff: staff & access ───────────────────────
  // Recruiting pros we do not have, and messaging the ones we do
  // (WALLIYO-0132). High: a campaign reaches everybody at once, and a lead
  // list is people's names and phone numbers.
  defineStaffFeatureEnablement({
    settingId: "MANAGE_LEADS_STAFF_FEATURE_ENABLEMENT",
    name: "Sales and growth",
    category: "leads",
    sensitivity: "high",
  }),
  // The market itself: what was asked for, who can do it, and where nobody
  // can (WALLIYO-0132). A read of the marketplace, so `high` rather than
  // critical: it shows customers and pros by name.
  defineStaffFeatureEnablement({
    settingId: "VIEW_MARKETPLACE_STAFF_FEATURE_ENABLEMENT",
    name: "Marketplace operations",
    category: "jobs",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "INVITE_STAFF_STAFF_FEATURE_ENABLEMENT",
    name: "Invite to the team",
    category: "staff",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "DEACTIVATE_STAFF_STAFF_FEATURE_ENABLEMENT",
    name: "Deactivate a member",
    category: "staff",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "END_STAFF_SESSIONS_STAFF_FEATURE_ENABLEMENT",
    name: "End sessions",
    category: "staff",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "MANAGE_ROLES_STAFF_FEATURE_ENABLEMENT",
    name: "Manage roles",
    category: "staff",
    sensitivity: "critical",
  }),
  // Teams are org structure, not access: somebody who arranges who works on
  // what is not thereby somebody who decides what they may do (WALLIYO-0131).
  defineStaffFeatureEnablement({
    settingId: "MANAGE_TEAMS_STAFF_FEATURE_ENABLEMENT",
    name: "Manage teams",
    category: "staff",
    sensitivity: "high",
  }),
  // The HR function: recruiting, the employment record, payroll, review
  // cycles and the course catalogue. Deliberately not what it takes to ask for
  // leave or to decide somebody's — everybody has leave, and it is decided by
  // whoever leads their team (WALLIYO-0131).
  defineStaffFeatureEnablement({
    settingId: "MANAGE_HUMAN_RESOURCES_STAFF_FEATURE_ENABLEMENT",
    name: "Human Resources",
    category: "staff",
    sensitivity: "high",
  }),
  // Deciding what one person may do, action by action. Critical for the same
  // reason MANAGE_ROLES is: whoever holds it decides what anybody else may do,
  // and can reach a single person without touching a role (WALLIYO-0131).
  defineStaffFeatureEnablement({
    settingId: "MANAGE_ACTION_ENABLEMENTS_STAFF_FEATURE_ENABLEMENT",
    name: "Manage action enablements",
    category: "staff",
    sensitivity: "critical",
  }),
  defineStaffFeatureEnablement({
    settingId: "ASSIGN_ROLES_STAFF_FEATURE_ENABLEMENT",
    name: "Assign roles",
    category: "staff",
    sensitivity: "critical",
  }),

  // ── Feature enablements for staff: audit ────────────────────────────────
  defineStaffFeatureEnablement({
    settingId: "VIEW_AUDIT_LOG_STAFF_FEATURE_ENABLEMENT",
    name: "Read the audit log",
    category: "audit",
    sensitivity: "high",
  }),
  defineStaffFeatureEnablement({
    settingId: "VIEW_SENSITIVE_AUDIT_VALUES_STAFF_FEATURE_ENABLEMENT",
    name: "View sensitive values",
    category: "audit",
    sensitivity: "critical",
  }),
  defineStaffFeatureEnablement({
    settingId: "VIEW_HEALTH_DASHBOARD_STAFF_FEATURE_ENABLEMENT",
    name: "View the dashboard",
    category: "audit",
    sensitivity: "low",
  }),

  // ── Feature enablements for clients ─────────────────────────────────────
  defineUserFeatureEnablement({
    settingId: "SEARCH_PROS_USER_FEATURE_ENABLEMENT",
    name: "Search for pros",
    category: "discovery",
    scopes: CLIENT_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "NEARBY_PROS_USER_FEATURE_ENABLEMENT",
    name: "Pros nearby",
    category: "discovery",
    scopes: CLIENT_FEATURE,
    defaultValue: true,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "REQUEST_PRO_USER_FEATURE_ENABLEMENT",
    name: "Request a pro",
    category: "jobs",
    scopes: CLIENT_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "POST_OPEN_JOB_USER_FEATURE_ENABLEMENT",
    name: "Post an open job request",
    category: "jobs",
    scopes: CLIENT_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "JOB_PHOTOS_USER_FEATURE_ENABLEMENT",
    name: "Photos in a job request",
    category: "jobs",
    scopes: CLIENT_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "SELECT_QUOTE_USER_FEATURE_ENABLEMENT",
    name: "Choose a quote",
    category: "jobs",
    scopes: CLIENT_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "LEAVE_REVIEWS_USER_FEATURE_ENABLEMENT",
    name: "Leave a review",
    category: "jobs",
    scopes: CLIENT_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "REQUEST_CALLBACK_USER_FEATURE_ENABLEMENT",
    name: "Request a callback",
    category: "support",
    scopes: CLIENT_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "SAVE_FAVORITE_PROS_USER_FEATURE_ENABLEMENT",
    name: "Favourite pros",
    category: "discovery",
    scopes: CLIENT_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "REBOOK_PRO_USER_FEATURE_ENABLEMENT",
    name: "Rebook a pro",
    category: "jobs",
    scopes: CLIENT_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "SHARE_PRO_PROFILE_USER_FEATURE_ENABLEMENT",
    name: "Share a pro's profile",
    category: "discovery",
    scopes: CLIENT_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "SCHEDULE_JOB_USER_FEATURE_ENABLEMENT",
    name: "Schedule a job request",
    category: "jobs",
    scopes: CLIENT_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "URGENT_JOB_USER_FEATURE_ENABLEMENT",
    name: "Urgent job request",
    category: "jobs",
    scopes: CLIENT_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),

  // ── Feature enablements for pros ────────────────────────────────────────
  defineUserFeatureEnablement({
    settingId: "BECOME_PRO_USER_FEATURE_ENABLEMENT",
    name: "Become a pro",
    category: "onboarding",
    scopes: CLIENT_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "LEAD_PREFERENCES_USER_FEATURE_ENABLEMENT",
    name: "Lead preferences",
    category: "leads",
    scopes: PRO_FEATURE,
    defaultValue: true,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "EDIT_RATES_USER_FEATURE_ENABLEMENT",
    name: "Edit rates",
    category: "profile",
    scopes: PRO_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "BROWSE_OPEN_JOBS_USER_FEATURE_ENABLEMENT",
    name: "Browse open job requests",
    category: "leads",
    scopes: PRO_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "QUOTE_JOBS_USER_FEATURE_ENABLEMENT",
    name: "Quote on job requests",
    category: "leads",
    scopes: PRO_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "PRO_SUBSCRIPTIONS_USER_FEATURE_ENABLEMENT",
    name: "Pro subscriptions",
    category: "payments",
    scopes: PRO_FEATURE,
    defaultValue: true,
    sensitivity: "high",
  }),
  defineUserFeatureEnablement({
    settingId: "SUBMIT_VERIFICATION_USER_FEATURE_ENABLEMENT",
    name: "Submit verification",
    category: "onboarding",
    scopes: PRO_FEATURE,
    // On by default: no pro can be found or hired until verified, so every
    // pro must be able to submit. Switched off per pro to stop one resubmitting.
    defaultValue: true,
    sensitivity: "high",
  }),
  defineUserFeatureEnablement({
    settingId: "PAUSE_LEADS_USER_FEATURE_ENABLEMENT",
    name: "Pause leads",
    category: "leads",
    scopes: PRO_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "PORTFOLIO_PHOTOS_USER_FEATURE_ENABLEMENT",
    name: "Portfolio photos",
    category: "profile",
    scopes: PRO_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "QUICK_REPLIES_USER_FEATURE_ENABLEMENT",
    name: "Quick replies",
    category: "messaging",
    scopes: PRO_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "ACTIVITY_PAGE_USER_FEATURE_ENABLEMENT",
    name: "Activity page",
    category: "profile",
    scopes: PRO_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "DECLARE_JOB_OUTCOME_USER_FEATURE_ENABLEMENT",
    name: "Declare a job's outcome",
    category: "jobs",
    scopes: CUSTOMER_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  // New leads reach a pro as in-app notifications, never SMS (WALLIYO-0098).
  defineUserFeatureEnablement({
    settingId: "LEAD_NOTIFICATIONS_USER_FEATURE_ENABLEMENT",
    name: "Leads as notifications",
    category: "notifications",
    scopes: PRO_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "SMS_LEADS_USER_FEATURE_ENABLEMENT",
    name: "Leads by SMS",
    category: "notifications",
    scopes: PRO_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "retired",
  }),
  // Reminders before a scheduled job: in-app notifications, never SMS.
  defineUserFeatureEnablement({
    settingId: "JOB_REMINDERS_USER_FEATURE_ENABLEMENT",
    name: "Job request reminders",
    category: "notifications",
    scopes: CUSTOMER_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),

  // ── Feature enablements for clients and pros ────────────────────────────
  defineUserFeatureEnablement({
    settingId: "SWITCH_MODE_USER_FEATURE_ENABLEMENT",
    name: "Switch between customer and pro",
    category: "accounts",
    scopes: CUSTOMER_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "MESSAGING_USER_FEATURE_ENABLEMENT",
    name: "Messaging",
    category: "messaging",
    scopes: CUSTOMER_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "SEND_PHOTOS_USER_FEATURE_ENABLEMENT",
    name: "Send photos",
    category: "messaging",
    scopes: CUSTOMER_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "SHARE_LOCATION_USER_FEATURE_ENABLEMENT",
    name: "Share location",
    category: "messaging",
    scopes: CUSTOMER_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "CHANGE_PHONE_USER_FEATURE_ENABLEMENT",
    name: "Change phone number",
    category: "accounts",
    scopes: CUSTOMER_FEATURE,
    defaultValue: true,
    sensitivity: "high",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "CHANGE_EMAIL_USER_FEATURE_ENABLEMENT",
    name: "Change email",
    category: "accounts",
    scopes: CUSTOMER_FEATURE,
    defaultValue: true,
    sensitivity: "high",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "CONTACT_US_USER_FEATURE_ENABLEMENT",
    name: "Contact us",
    category: "support",
    scopes: CUSTOMER_FEATURE,
    defaultValue: true,
    sensitivity: "low",
  }),
  defineUserFeatureEnablement({
    settingId: "CREATE_CLIENT_PROFILE_USER_FEATURE_ENABLEMENT",
    name: "Create a client profile",
    category: "accounts",
    scopes: CUSTOMER_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "REPORT_USER_USER_FEATURE_ENABLEMENT",
    name: "Report someone",
    category: "safety",
    scopes: CUSTOMER_FEATURE,
    defaultValue: true,
    sensitivity: "high",
  }),
  defineUserFeatureEnablement({
    settingId: "BLOCK_USER_USER_FEATURE_ENABLEMENT",
    name: "Block someone",
    category: "safety",
    scopes: CUSTOMER_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "APPEAL_DECISION_USER_FEATURE_ENABLEMENT",
    name: "Appeal a decision",
    category: "safety",
    scopes: CUSTOMER_FEATURE,
    defaultValue: true,
    sensitivity: "high",
  }),
  defineUserFeatureEnablement({
    settingId: "SECURITY_ACTIVITY_USER_FEATURE_ENABLEMENT",
    name: "Security activity",
    category: "accounts",
    scopes: CUSTOMER_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),
  defineUserFeatureEnablement({
    settingId: "REFER_FRIENDS_USER_FEATURE_ENABLEMENT",
    name: "Refer friends",
    category: "accounts",
    scopes: CUSTOMER_FEATURE,
    defaultValue: false,
    sensitivity: "low",
    status: "planned",
  }),

  // Account deletion is deliberately not here: the App Store requires it to be
  // available to everyone, always, so it must never be switchable.
] satisfies readonly Setting[];

export type SettingId = (typeof SETTINGS)[number]["settingId"];

/** One specific setting's value, e.g. SettingValueOf<"LEAD_RADIUS_USER_SETTING"> is number. */
export type SettingValueOf<I extends SettingId> = Extract<(typeof SETTINGS)[number], { settingId: I }>["defaultValue"];
